import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn, UserPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      if (mode === 'login') await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      nav(params.get('next') || '/account');
    } catch (ex) {
      setErr(ex.message);
      setBusy(false);
    }
  };

  const input =
    'w-full bg-white border border-emerald-950/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-flame transition';
  const label = 'font-mono text-[10px] uppercase tracking-widest text-sub block mb-1.5';

  return (
    <div className="bg-forest noise relative min-h-screen grid place-items-center px-4 py-20">
      <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full border border-white/5 spin-slow pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md bg-cream rounded-3xl p-8 sm:p-10 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)] relative"
      >
        <p className="font-mono text-[10px] uppercase tracking-widest2 text-flame mb-2">Your account</p>
        <h1 className="font-display font-extrabold uppercase tracking-tight text-forest text-4xl leading-none">
          {mode === 'login' ? 'Sign in' : 'Create account'}
        </h1>
        <p className="text-sm text-sub mt-2 mb-7">
          {mode === 'login'
            ? 'Track orders and check out faster.'
            : 'Join to save your garage and see order history.'}
        </p>

        <form onSubmit={submit} className="space-y-4">
          {mode === 'register' && (
            <div>
              <label className={label}>Full name</label>
              <input data-testid="register-name-input" required className={input} value={form.name} onChange={set('name')} placeholder="Sam Whitfield" />
            </div>
          )}
          <div>
            <label className={label}>Email</label>
            <input data-testid="auth-email-input" type="email" required className={input} value={form.email} onChange={set('email')} placeholder="sam@farmmail.com" />
          </div>
          <div>
            <label className={label}>Password</label>
            <input data-testid="auth-password-input" type="password" required minLength={8} className={input} value={form.password} onChange={set('password')} placeholder={mode === 'register' ? '8+ characters' : '••••••••'} />
          </div>
          {err && <p data-testid="auth-error-message" className="text-sm text-bad bg-bad/10 rounded-xl px-4 py-2.5">{err}</p>}
          <button
            data-testid="auth-submit-button"
            disabled={busy}
            className="w-full inline-flex justify-center items-center gap-2 bg-flame hover:bg-ember disabled:opacity-50 text-white font-display font-semibold uppercase tracking-wide px-8 py-4 rounded-full transition-colors"
          >
            {mode === 'login' ? <LogIn className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
            {busy ? 'Working…' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <div className="flex items-center gap-3 my-6">
          <span className="flex-1 h-px bg-emerald-950/10" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-sub">or</span>
          <span className="flex-1 h-px bg-emerald-950/10" />
        </div>
        <button
          data-testid="google-signin-button"
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + '/account';
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="w-full inline-flex justify-center items-center gap-2.5 bg-white border-2 border-emerald-950/15 hover:border-flame text-ink font-display font-semibold uppercase tracking-wide px-8 py-4 rounded-full transition-colors"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
            <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
            <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
            <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
            <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
          </svg>
          Continue with Google
        </button>

        <p className="text-sm text-sub text-center mt-6">
          {mode === 'login' ? (
            <>No account yet?{' '}
              <button data-testid="switch-to-register" onClick={() => { setMode('register'); setErr(null); }} className="text-flame font-semibold hover:underline">Create one</button>
            </>
          ) : (
            <>Already registered?{' '}
              <button data-testid="switch-to-login" onClick={() => { setMode('login'); setErr(null); }} className="text-flame font-semibold hover:underline">Sign in</button>
            </>
          )}
        </p>
        <p className="text-xs text-sub text-center mt-4">
          <Link to="/shop" className="hover:text-flame">← Continue as guest and keep shopping</Link>
        </p>
      </motion.div>
    </div>
  );
}
